import { inject, Injectable } from '@angular/core';
import { catchError, from, map, Observable, of, switchMap } from 'rxjs';
import { b64ToBlob, blobToB64 } from '../../shared/helpers/file';
import { doSupabaseCall } from '../../shared/helpers/supabase.helper';
import type { IAnswersSnapshot, IAttachment, IInterviewMeta, IInterviewRow } from '../../shared/models';
import { AuthService } from '../auth/auth.service';
import { SupabaseService } from '../supabase/supabase';

const BUCKET = 'interview-pdfs';

// Archivio interviste su Supabase. Niente filtri ruolo qui: li fa la RLS (fk_auth_users_id/store).
@Injectable({ providedIn: 'root' })
export class InterviewService {
  // --- private ---
  private readonly _supabase: SupabaseService = inject(SupabaseService);
  private readonly _auth: AuthService = inject(AuthService);

  // --- public ---
  // Crea la riga (store fotografato dal profilo dell'architetto loggato) → id tecnico + codice parlante.
  public create(meta: IInterviewMeta): Observable<{ id: string; code: number } | null> {
    const client = this._supabase.client;
    const session = this._auth.sessionSignal();
    if (null === client || null === session) {
      return of(null);
    }
    return doSupabaseCall<{ id: string; code: number }>(
      client
        .from('interview_app')
        .insert({
          fk_auth_users_id: session.user.id,
          fk_store_id: this._auth.profileSignal()?.fk_store_id ?? null,
          client_names: meta.client_names,
          percorso: meta.percorso,
          interview_date: meta.interview_date,
        })
        .select('id, code')
        .single(),
    );
  }

  // Upload (upsert: rigenerazione = stesso path) + aggiornamento pdf_path sulla riga.
  public uploadPdf(code: number, blob: Blob): Observable<void> {
    const client = this._supabase.client;
    const session = this._auth.sessionSignal();
    if (null === client || null === session) {
      return of(undefined);
    }
    const path = `${session.user.id}/${code}.pdf`;
    return doSupabaseCall(
      client.storage.from(BUCKET).upload(path, blob, {
        upsert: true,
        contentType: 'application/pdf',
      }),
    ).pipe(
      switchMap(() =>
        doSupabaseCall(client.from('interview_app').update({ pdf_path: path }).eq('code', code)),
      ),
      map(() => undefined),
    );
  }

  // Scheda dell'Abitazione (PDF caricato o .docx generato dal form) → stesso bucket del profilo,
  // path distinto tramite suffisso `_scheda`.
  public uploadScheda(code: number, blob: Blob, ext: string): Observable<void> {
    const client = this._supabase.client;
    const session = this._auth.sessionSignal();
    if (null === client || null === session) {
      return of(undefined);
    }
    const path = `${session.user.id}/${code}_scheda.${ext}`;
    return doSupabaseCall(
      client.storage.from(BUCKET).upload(path, blob, {
        upsert: true,
        contentType: blob.type,
      }),
    ).pipe(
      switchMap(() =>
        doSupabaseCall(
          client.from('interview_app').update({ scheda_path: path }).eq('code', code),
        ),
      ),
      map(() => undefined),
    );
  }

  // Il Profilo Abitativo da solo ({code}_abitativo.pdf) e la scheda compilata da form
  // ({code}_scheda.pdf) li genera e carica il BACKEND insieme al PDF completo
  // (be/.../generation.service._buildAndUploadPdf): qui resta solo l'upload della scheda
  // caricata come file dall'utente (uploadScheda).

  public list(): Observable<IInterviewRow[]> {
    const client = this._supabase.client;
    if (null === client) {
      return of([]);
    }
    return doSupabaseCall<IInterviewRow[]>(
      client
        .from('interview_app')
        .select(
          'id, code, client_names, percorso, participant_app(gruppo_ricerca), interview_question_set_app(scelto:select_percorso!fk_select_percorso_id_scelto(code)), interview_date, created_at:date_cre, pdf_path, scheda_path, abitativo_path, data_presentazione_progetto, data_ingresso_casa, profile_app(denomination), interview_answers_app(id, somministratore:payload->>somministratore, sessionMode:payload->>sessionMode, timing:payload->timing)',
        )
        // Soft delete (0028): le righe timbrate spariscono dalla lista, i dati restano.
        .is('date_del', null)
        .order('code', { ascending: false })
        .returns<IInterviewRow[]>(),
    ).pipe(
      // Migrazioni 0026/0027/0028 non ancora eseguite → embed, colonna nuova o filtro date_del
      // fanno fallire TUTTA la query: si ricade sulla select pre-migrazioni (archivio visibile,
      // senza "Riprendi", download Abitativo né soft delete).
      catchError(() =>
        doSupabaseCall<IInterviewRow[]>(
          client
            .from('interview_app')
            .select(
              'id, code, client_names, percorso, participant_app(gruppo_ricerca), interview_date, created_at:date_cre, pdf_path, scheda_path, data_presentazione_progetto, data_ingresso_casa, profile_app(denomination)',
            )
            .order('code', { ascending: false })
            .returns<IInterviewRow[]>(),
        ).pipe(
          map((rows) => rows.map((r) => ({ ...r, abitativo_path: null, interview_answers_app: null }))),
        ),
      ),
    );
  }

  // Aggiorna le due date di scadenza (presentazione progetto / ingresso casa) dall'archivio.
  public updateDates(
    id: string,
    dates: { data_presentazione_progetto: string | null; data_ingresso_casa: string | null },
  ): Observable<void> {
    const client = this._supabase.client;
    if (null === client) {
      return of(undefined);
    }
    return doSupabaseCall(client.from('interview_app').update(dates).eq('id', id)).pipe(
      map(() => undefined),
    );
  }

  // Riallinea i metadati della riga (nomi/percorso/data): la riga ora nasce al primo salvataggio
  // incrementale, quando questi campi possono essere ancora vuoti — si aggiornano a ogni snapshot.
  public updateMeta(id: string, meta: IInterviewMeta): Observable<void> {
    const client = this._supabase.client;
    if (null === client) {
      return of(undefined);
    }
    return doSupabaseCall(client.from('interview_app').update(meta).eq('id', id)).pipe(
      map(() => undefined),
    );
  }

  // Snapshot risposte (interview_answers_app): una riga per intervista, upsert — l'ultimo stato
  // vince, come ponderazione/fase zero. Rete di sicurezza per crash browser/generazione fallita.
  public saveAnswers(interviewId: string, payload: IAnswersSnapshot): Observable<void> {
    const client = this._supabase.client;
    const session = this._auth.sessionSignal();
    if (null === client || null === session) {
      return of(undefined);
    }
    return doSupabaseCall(
      client.from('interview_answers_app').upsert(
        {
          fk_interview_app_id: interviewId,
          payload,
          fk_auth_users_id: session.user.id,
        },
        { onConflict: 'fk_interview_app_id' },
      ),
    ).pipe(map(() => undefined));
  }

  // Codice parlante dall'id: serve al ripristino da URL (?intervista=…) dopo un refresh.
  public getCode(interviewId: string): Observable<number | null> {
    const client = this._supabase.client;
    if (null === client) {
      return of(null);
    }
    return doSupabaseCall<{ code: number } | null>(
      client
        .from('interview_app')
        .select('code')
        .eq('id', interviewId)
        .is('date_del', null)
        .maybeSingle(),
    ).pipe(map((row) => row?.code ?? null));
  }

  public getAnswers(interviewId: string): Observable<IAnswersSnapshot | null> {
    const client = this._supabase.client;
    if (null === client) {
      return of(null);
    }
    return doSupabaseCall<{ payload: IAnswersSnapshot } | null>(
      client
        .from('interview_answers_app')
        .select('payload')
        .eq('fk_interview_app_id', interviewId)
        .maybeSingle(),
    ).pipe(map((row) => row?.payload ?? null));
  }

  // Cancellazione NON distruttiva (soft delete, migrazione 0028): si timbra date_del e la riga
  // sparisce dalla lista. Righe figlie, snapshot e file su Storage restano intatti — per
  // ripristinare basta riportare date_del a NULL da DB.
  public softDeleteInterview(id: string): Observable<void> {
    const client = this._supabase.client;
    if (null === client) {
      return of(undefined);
    }
    return doSupabaseCall(
      client.from('interview_app').update({ date_del: new Date().toISOString() }).eq('id', id),
    ).pipe(map(() => undefined));
  }

  // Allegato utente (scheda caricata / documento risposte) → bucket Storage, MAI dentro la riga
  // DB (v. commento su IAttachment). Upsert: ri-salvataggi della stessa pratica sovrascrivono lo
  // stesso path. Ritorna il path da mettere nello snapshot; null = upload fallito (il chiamante
  // riproverà al prossimo autosave).
  public uploadAttachment(
    code: number,
    att: IAttachment,
    kind: 'scheda_doc' | 'answers_file',
  ): Observable<string | null> {
    const client = this._supabase.client;
    const session = this._auth.sessionSignal();
    if (null === client || null === session) {
      return of(null);
    }
    const ext = att.name.includes('.') ? att.name.slice(att.name.lastIndexOf('.') + 1) : 'bin';
    const path = `${session.user.id}/${code}_${kind}.${ext}`;
    return doSupabaseCall(
      client.storage.from(BUCKET).upload(path, b64ToBlob(att.b64, att.mediaType), {
        upsert: true,
        contentType: att.mediaType,
      }),
    ).pipe(
      map(() => path),
      catchError(() => of(null)),
    );
  }

  // Ri-scarica un allegato dal bucket e lo riconverte in base64 (resume dopo refresh: lo snapshot
  // porta solo il path, il b64 serve in memoria per i blocchi del prompt Anthropic).
  public downloadAttachmentB64(path: string): Observable<string | null> {
    const client = this._supabase.client;
    if (null === client) {
      return of(null);
    }
    return doSupabaseCall<Blob>(client.storage.from(BUCKET).download(path)).pipe(
      switchMap((blob) => from(blobToB64(blob))),
      catchError(() => of(null)),
    );
  }

  public signedUrl(pdfPath: string): Observable<string | null> {
    const client = this._supabase.client;
    if (null === client) {
      return of(null);
    }
    return doSupabaseCall<{ signedUrl: string }>(
      client.storage.from(BUCKET).createSignedUrl(pdfPath, 3600),
    ).pipe(map((row) => row.signedUrl));
  }
}
